// FAMILY-EXT: Встроенное превью страницы Wiki.js. Используется из
// Markdown-обёртки, когда в описании карточки встречается
// синтаксис `[[wiki:path]]`. Грузим страницу через Planka API
// (GET /api/wiki-pages?path=...) — авторизация через ту же сессию
// Planka, токен Wiki хранится только на сервере.

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import styles from './WikiEmbed.module.scss';

const ABS_URL_REGEX = /^(?:https?:)?\/\//i;

// Backend Wiki.js URL — публичный, чтобы ссылки/картинки внутри
// рендера резолвились браузером. Берётся из VITE-стиля env через
// window.__WIKI_PUBLIC_URL__ если задан, иначе вычисляется по
// текущему хосту (заменяем `tasks.` на `wiki.`).
function publicWikiBase() {
  // eslint-disable-next-line no-underscore-dangle
  if (typeof window !== 'undefined' && window.__WIKI_PUBLIC_URL__) {
    return window.__WIKI_PUBLIC_URL__;
  }
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (h.startsWith('tasks.')) {
      return `${window.location.protocol}//${h.replace(/^tasks\./, 'wiki.')}`;
    }
  }
  return '';
}

// HTML, который пришёл от Wiki.js, может содержать относительные
// ссылки (например `/architecture/diagrams`) — превращаем их в
// абсолютные через wiki-host, чтобы клики по ссылкам внутри
// рендера вели на саму Wiki в новом табе.
function absolutizeLinks(html, wikiBase) {
  if (!wikiBase) return html;
  return html
    .replace(/href="\/(?!\/)/g, `href="${wikiBase}/`)
    .replace(/src="\/(?!\/)/g, `src="${wikiBase}/`);
}

const WikiEmbed = React.memo(({ path }) => {
  const [state, setState] = useState({ status: 'loading' });
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const url = `/api/wiki-pages?path=${encodeURIComponent(path)}`;

    // Planka хранит JWT в обычной (НЕ httpOnly) cookie 'accessToken'
    // и шлёт его как Bearer в Authorization. Воспроизводим то же.
    const m = document.cookie.match(/(?:^|;\s*)accessToken=([^;]+)/);
    const accessToken = m ? decodeURIComponent(m[1]) : '';

    fetch(url, {
      credentials: 'include',
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    })
      .then(async (r) => {
        if (cancelled) return;
        if (!r.ok) {
          setState({ status: r.status === 404 ? 'notFound' : 'error' });
          return;
        }
        const data = await r.json();
        const page = data?.item;
        if (!page) {
          setState({ status: 'notFound' });
          return;
        }
        setState({ status: 'ok', page });
      })
      .catch((e) => {
        if (cancelled) return;
        setState({ status: 'error', message: String(e?.message || e) });
      });

    return () => {
      cancelled = true;
    };
  }, [path]);

  // Открыть страницу в Wiki в новом табе при клике на заголовок
  const wikiBase = publicWikiBase();
  const linkHref = state.page ? `${wikiBase}/${state.page.path}` : null;

  if (state.status === 'loading') {
    return (
      <div className={styles.wikiEmbed}>
        <div className={styles.wikiEmbedHeader}>📖 Wiki: загрузка…</div>
      </div>
    );
  }
  if (state.status === 'notFound') {
    return (
      <div className={styles.wikiEmbedError}>
        ⚠️ Wiki-страница «{path}» не найдена
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className={styles.wikiEmbedError}>
        ⚠️ Не удалось загрузить wiki: {state.message || 'ошибка'}
      </div>
    );
  }

  const { page } = state;
  const html = absolutizeLinks(page.html || '', wikiBase);

  return (
    <div className={styles.wikiEmbed}>
      <div className={styles.wikiEmbedHeader}>
        📖{' '}
        {linkHref ? (
          <a
            href={linkHref}
            target="_blank"
            rel="noreferrer"
            className={styles.wikiEmbedTitle}
          >
            {page.title}
          </a>
        ) : (
          <span className={styles.wikiEmbedTitle}>{page.title}</span>
        )}
      </div>
      {page.description && (
        <div className={styles.wikiEmbedDescription}>{page.description}</div>
      )}
      <div
        ref={containerRef}
        className={styles.wikiEmbedBody}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
});

WikiEmbed.propTypes = {
  path: PropTypes.string.isRequired,
};

WikiEmbed.absoluteUrlRegex = ABS_URL_REGEX;

export default WikiEmbed;
